import { ReportDefinition } from '../models/report-definition';
import { ReportRowVm } from '../models/report-row-vm';

const LAYOUT_DETAILED_ADJUSTMENTS = 'detailed-adjustments';
const LAYOUT_ADJUSTMENTS_BY_USER = 'adjustments-by-user';

type RevenueAdjustmentRow = {
   rowId: string;
   sourceRow: any;
   date: string;
   dateRaw: string;
   time: string;
   room: string;
   guestName: string;
   transaction: string;
   transactionCode: string;
   transactionDescription: string;
   reason: string;
   amount: string;
   amountRaw: number;
   user: string;
   hasRealTyallsRowId: boolean;
};

type AdjustmentUserSummary = {
   user: string;
   count: number;
   totalAmount: number;
};

const TRANSACTION_CODE_DESCRIPTIONS: Record<string, string> = {
   '1000': 'Accommodation',
   '1010': 'Sundry Accommodation',
   '1249': 'Adjustment Accommodation',
   '1250': 'Movies',
   '2000': 'Breakfast',
   '2010': 'Breakfast Beverage',
   '2015': 'Breakfast Surcharge',
   '2040': 'Dinner Food',
   '2050': 'Dinner Beverage',
   '2120': 'Lunch Food',
   '3020': 'Lunch Food',
   '3030': 'Lunch Beverage',
   '3040': 'Dinner Food',
   '3050': 'Dinner Beverage',
   '4000': 'Room Service Breakfast',
   '4010': 'Room Service Breakfast Beverage',
   '4040': 'Room Service Dinner',
   '4050': 'Room Service Dinner Beverage',
   '4090': 'Room Service Tray Charge',
   '4500': 'Minibar',
   '4510': 'Minibar Beverage',
   '4599': 'Minibar Denial',
   '5040': 'Function Revenue',
   '5100': 'Function Morning Tea Food',
   '5120': 'Function Revenue',
   '5200': 'Venue Hire',
   '7405': 'Credit Card Transaction Fee',
   '7550': 'Promotions Credit',
   '9920': 'Global Blue Payments'
};

function asString(value: any): string {
   if (value === null || value === undefined) return '';
   return String(value);
}

function parseReportJson(reportJson: any): any {
   if (typeof reportJson !== 'string') return reportJson;

   try {
      return JSON.parse(reportJson);
   } catch {
      return {};
   }
}

function getPathValue(source: any, path: string): any {
   if (!source || !path) return undefined;

   return path.split('.').reduce((current, part) => current?.[part], source);
}

function getFirstValue(source: any, paths: string[]): any {
   for (const path of paths) {
      const value = getPathValue(source, path);

      if (value !== null && value !== undefined && value !== '') {
         return value;
      }
   }

   return undefined;
}

function getFirstString(source: any, paths: string[]): string {
   return asString(getFirstValue(source, paths));
}

function toNumberOrNull(value: any): number | null {
   if (value === null || value === undefined || value === '') return null;

   if (typeof value === 'number') {
      return Number.isFinite(value) ? value : null;
   }

   const cleaned = String(value)
      .replace(/,/g, '')
      .replace(/\s+/g, '')
      .replace(/^-\s*/, '-');

   const amount = Number(cleaned);
   return Number.isFinite(amount) ? amount : null;
}

function getFirstNumber(source: any, paths: string[]): number | null {
   for (const path of paths) {
      const amount = toNumberOrNull(getPathValue(source, path));

      if (amount !== null) return amount;
   }

   return null;
}

function formatCurrency(value: number): string {
   const formatted = Math.abs(value).toLocaleString('en-AU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
   });

   return value < 0 ? `-$${formatted}` : `$${formatted}`;
}

function formatDate(value: any): string {
   const raw = asString(value).trim();
   if (!raw) return '';

   const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
   if (isoMatch) {
      return `${isoMatch[3]}/${isoMatch[2]}/${isoMatch[1]}`;
   }

   const slashMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})/);
   if (slashMatch) {
      return `${slashMatch[1].padStart(2, '0')}/${slashMatch[2].padStart(
         2,
         '0'
      )}/${slashMatch[3]}`;
   }

   return raw;
}

function formatTime(value: any): string {
   const raw = asString(value).trim();
   if (!raw) return '';

   const isoTimeMatch = raw.match(/T(\d{2}:\d{2})/);
   if (isoTimeMatch) return isoTimeMatch[1];

   const simpleTimeMatch = raw.match(/^(\d{1,2}):(\d{2})/);
   if (simpleTimeMatch) {
      return `${simpleTimeMatch[1].padStart(2, '0')}:${simpleTimeMatch[2]}`;
   }

   return raw;
}

function getRealTyallsRowId(row: any): string {
   return asString(row?.tyallsRowId ?? row?.TyallsRowId);
}

function getRowId(row: any): string {
   const realTyallsRowId = getRealTyallsRowId(row);
   if (realTyallsRowId) return realTyallsRowId;

   return (
      getFirstString(row, [
         'TransactionNo',
         'transactionNo',
         'ReceiptNo',
         'receiptNo',
         'Id',
         'id'
      ]) ||
      `revenue-adjustment:${getFirstString(row, [
         'Date',
         'date',
         'TransactionDate',
         'transactionDate',
         'PostingDate',
         'postingDate'
      ])}:${getFirstString(row, ['Time', 'time', 'transactionTime'])}:${getFirstString(
         row,
         ['RoomNumber', 'roomNumber', 'guestInfo.roomId']
      )}:${getFirstString(row, ['TransactionCode', 'transactionCode'])}`
   );
}

function getSourceRows(reportJson: any): { rows: any[]; isOracle: boolean } {
   const parsed = parseReportJson(reportJson);

   if (Array.isArray(parsed?.PmsRevenueAdjustments)) {
      return { rows: parsed.PmsRevenueAdjustments, isOracle: false };
   }

   if (Array.isArray(parsed?.PMSRevenueAdjustments)) {
      return { rows: parsed.PMSRevenueAdjustments, isOracle: false };
   }

   if (Array.isArray(parsed?.RevenueAdjustments)) {
      return { rows: parsed.RevenueAdjustments, isOracle: false };
   }

   if (Array.isArray(parsed?.Rows)) {
      return { rows: parsed.Rows, isOracle: false };
   }

   if (Array.isArray(parsed?.rows)) {
      return { rows: parsed.rows, isOracle: false };
   }

   if (Array.isArray(parsed?.Postings)) {
      return { rows: parsed.Postings, isOracle: false };
   }

   if (Array.isArray(parsed?.financialPostings)) {
      return { rows: parsed.financialPostings, isOracle: true };
   }

   if (Array.isArray(parsed?.journalPostings?.postings)) {
      return { rows: parsed.journalPostings.postings, isOracle: true };
   }

   if (Array.isArray(parsed?.JournalPostings?.Postings)) {
      return { rows: parsed.JournalPostings.Postings, isOracle: true };
   }

   return { rows: [], isOracle: false };
}

function amountFromMappedPostingDollars(row: any): number | null {
   const directAmount = getFirstNumber(row, [
      'CreditAdjustment',
      'creditAdjustment',
      'DebitRevenue',
      'debitRevenue',
      'Amount',
      'amount',
      'AmountRaw',
      'amountRaw',
      'AdjustmentAmount',
      'adjustmentAmount'
   ]);

   if (directAmount !== null && directAmount < 0) {
      return directAmount;
   }

   const debitCents = getFirstNumber(row, [
      'DebitAmount.Amount',
      'debitAmount.amount',
      'DebitAmount',
      'debitAmount'
   ]);

   if (debitCents !== null && debitCents < 0) {
      return debitCents / 100;
   }

   const postedCents = getFirstNumber(row, [
      'PostedAmount.Amount',
      'postedAmount.amount',
      'PostedAmount',
      'postedAmount'
   ]);

   if (postedCents !== null && postedCents < 0) {
      return Math.abs(postedCents) > 999 ? postedCents / 100 : postedCents;
   }

   return null;
}

function amountFromOraclePostingDollars(row: any): number | null {
   const debitAmount = getFirstNumber(row, [
      'debitAmount.amount',
      'DebitAmount.Amount',
      'DebitAmount',
      'debitAmount'
   ]);

   if (debitAmount !== null && debitAmount < 0) {
      return debitAmount;
   }

   const postedAmount = getFirstNumber(row, [
      'postedAmount.amount',
      'amount.amount',
      'PostedAmount.Amount',
      'PostedAmount',
      'Amount',
      'amount'
   ]);

   if (postedAmount !== null && postedAmount < 0) {
      return postedAmount;
   }

   return null;
}

function getDate(row: any): string {
   return getFirstString(row, [
      'Date',
      'date',
      'TransactionDate',
      'transactionDate',
      'PostingDate',
      'postingDate',
      'BusinessDate',
      'businessDate'
   ]);
}

function getTime(row: any): string {
   return getFirstString(row, [
      'Time',
      'time',
      'TransactionTime',
      'transactionTime',
      'lastModifyDateTime',
      'LastModifyDateTime'
   ]);
}

function getRoom(row: any): string {
   return getFirstString(row, [
      'RoomNumber',
      'roomNumber',
      'RoomNo',
      'roomNo',
      'Room',
      'room',
      'guestInfo.roomId',
      'guestInfo.roomNumber'
   ]);
}

function getGuestName(row: any): string {
   return getFirstString(row, [
      'GuestName',
      'guestName',
      'Name',
      'name',
      'guestInfo.guestName'
   ]);
}

function getTransactionCode(row: any): string {
   return getFirstString(row, [
      'TransactionCode',
      'transactionCode',
      'TrnCode',
      'trnCode'
   ]);
}

function getTransactionDescription(row: any): string {
   const transactionCode = getTransactionCode(row);

   return (
      getFirstString(row, [
         'TransactionDescription',
         'transactionDescription',
         'TransactionCodeDescription',
         'transactionCodeDescription',
         'CodeDescription',
         'codeDescription'
      ]) ||
      TRANSACTION_CODE_DESCRIPTIONS[transactionCode] ||
      getFirstString(row, ['Description', 'description'])
   );
}

function getTransactionDisplay(row: any): string {
   const code = getTransactionCode(row);
   const description = getTransactionDescription(row);

   if (code && description) return `${description} (${code})`;
   if (description) return description;
   return code;
}

function getReason(row: any): string {
   const values = [
      getFirstString(row, [
         'Reason',
         'reason',
         'DescriptionReference',
         'descriptionReference',
         'TransactionRemark',
         'transactionRemark',
         'Remark',
         'remark',
         'Reference',
         'reference'
      ]),
      getFirstString(row, ['Description', 'description'])
   ].filter(Boolean);

   return Array.from(new Set(values)).join(' ');
}

function getUser(row: any): string {
   const direct = getFirstString(row, [
      'User',
      'user',
      'CashIdUser',
      'cashIdUser',
      'PostedByCashier',
      'postedByCashier',
      'CashierId',
      'cashierId',
      'cashierInfo.cashierId'
   ]);

   if (direct) return direct;

   return getFirstString(row, ['UserName', 'userName', 'cashierInfo.userName']);
}

function normaliseAdjustmentRows(
   sourceRows: any[],
   isOracle: boolean
): RevenueAdjustmentRow[] {
   const rows: RevenueAdjustmentRow[] = [];

   for (const sourceRow of sourceRows) {
      const amountRaw = isOracle
         ? amountFromOraclePostingDollars(sourceRow)
         : amountFromMappedPostingDollars(sourceRow);

      if (amountRaw === null || amountRaw >= 0) continue;

      const dateRaw = getDate(sourceRow);

      rows.push({
         rowId: getRowId(sourceRow),
         sourceRow,
         date: formatDate(dateRaw),
         dateRaw,
         time: formatTime(getTime(sourceRow)),
         room: getRoom(sourceRow),
         guestName: getGuestName(sourceRow),
         transaction: getTransactionDisplay(sourceRow),
         transactionCode: getTransactionCode(sourceRow),
         transactionDescription: getTransactionDescription(sourceRow),
         reason: getReason(sourceRow),
         amount: formatCurrency(amountRaw),
         amountRaw,
         user: getUser(sourceRow),
         hasRealTyallsRowId: !!getRealTyallsRowId(sourceRow)
      });
   }

   return rows;
}

function getAdjustmentRows(reportJson: any): RevenueAdjustmentRow[] {
   const { rows: sourceRows, isOracle } = getSourceRows(reportJson);

   return sortAdjustmentRows(normaliseAdjustmentRows(sourceRows, isOracle));
}

function sortAdjustmentRows(rows: RevenueAdjustmentRow[]): RevenueAdjustmentRow[] {
   return [...rows].sort((a, b) => {
      const dateCompare = a.dateRaw.localeCompare(b.dateRaw);
      if (dateCompare !== 0) return dateCompare;

      return a.time.localeCompare(b.time);
   });
}

function buildAdjustmentUserSummaries(
   rows: RevenueAdjustmentRow[]
): AdjustmentUserSummary[] {
   const totals = new Map<string, AdjustmentUserSummary>();

   for (const row of rows) {
      const user = row.user || 'Unknown';

      const current = totals.get(user) ?? {
         user,
         totalAmount: 0,
         count: 0
      };

      current.totalAmount += row.amountRaw;
      current.count += 1;

      totals.set(user, current);
   }

   return Array.from(totals.values()).sort((a, b) => {
      const aNumber = Number(a.user);
      const bNumber = Number(b.user);

      if (Number.isFinite(aNumber) && Number.isFinite(bNumber)) {
         return aNumber - bNumber;
      }

      return a.user.localeCompare(b.user);
   });
}

function flattenDetailedAdjustments(reportJson: any): ReportRowVm[] {
   const adjustmentRows = getAdjustmentRows(reportJson);

   return adjustmentRows.map((row) => ({
      rowId: row.rowId,
      isAnnotatable: row.hasRealTyallsRowId,
      cells: {
         rowType: 'transaction',
         date: row.date,
         dateRaw: row.dateRaw,
         time: row.time,
         room: row.room,
         guestName: row.guestName,
         transaction: row.transaction,
         transactionCode: row.transactionCode,
         transactionDescription: row.transactionDescription,
         reason: row.reason,
         amount: row.amount,
         amountRaw: row.amountRaw,
         user: row.user
      }
   }));
}

function flattenAdjustmentsByUser(reportJson: any): ReportRowVm[] {
   const adjustmentRows = getAdjustmentRows(reportJson);
   const userSummaries = buildAdjustmentUserSummaries(adjustmentRows);

   return userSummaries.map((summary) => ({
      rowId: `pms-revenue-adjustments:${LAYOUT_ADJUSTMENTS_BY_USER}:user:${summary.user}`,
      isAnnotatable: false,
      cells: {
         rowType: 'userAdjustmentSummary',
         user: summary.user === 'Unknown' ? 'Unknown' : `User ${summary.user}`,
         userRaw: summary.user,
         count: summary.count,
         totalAdjusted: formatCurrency(summary.totalAmount),
         totalAdjustedRaw: summary.totalAmount
      }
   }));
}

function getHeaderTotal(rows: ReportRowVm[], layoutId?: string | null): number {
   if (layoutId === LAYOUT_ADJUSTMENTS_BY_USER) {
      return rows.reduce((total, row) => {
         const amount = Number(row.cells?.['totalAdjustedRaw']);
         return Number.isFinite(amount) ? total + amount : total;
      }, 0);
   }

   return rows.reduce((total, row) => {
      const amount = Number(row.cells?.['amountRaw']);
      return Number.isFinite(amount) ? total + amount : total;
   }, 0);
}

export const PMS_REVENUE_ADJUSTMENTS_DEFINITION: ReportDefinition = {
   reportType: 'PmsRevenueAdjustments',
   version: 4,
   title: 'PMS Revenue Adjustments',

   sorting: {
      enabled: true,
      persist: true
   },

   header: {
      title: 'Revenue Adjustments',
      snippets: ({ allRows, layoutId }) => {
         const totalAdjusted = getHeaderTotal(allRows, layoutId);

         const count =
            layoutId === LAYOUT_ADJUSTMENTS_BY_USER
               ? allRows.filter(
                    (row) => row.cells?.['rowType'] === 'userAdjustmentSummary'
                 ).length
               : allRows.filter((row) => row.cells?.['rowType'] === 'transaction')
                    .length;

         return [
            {
               label: layoutId === LAYOUT_ADJUSTMENTS_BY_USER ? 'Users' : 'Count',
               value: count
            },
            {
               label: 'Total Adjusted',
               value: formatCurrency(totalAdjusted),
               tone: totalAdjusted < 0 ? 'danger' : 'neutral'
            }
         ];
      }
   },

   rowChecks: {
      enabled: false,
      includeNestedRows: false,
      selectableRowTypes: ['transaction']
   },

   annotations: false,

   columns: [
      {
         key: 'date',
         header: 'TRN DATE',
         width: '105px',
         sort: {
            type: 'date',
            getValue: (row) => row.cells['dateRaw']
         }
      },
      {
         key: 'time',
         header: 'TIME',
         width: '75px',
         sort: { type: 'string' }
      },
      {
         key: 'room',
         header: 'ROOM',
         width: '80px',
         sort: { type: 'string' }
      },
      {
         key: 'guestName',
         header: 'GUEST NAME',
         width: '160px',
         sort: { type: 'string' }
      },
      {
         key: 'transaction',
         header: 'TRANSACTION',
         width: '180px',
         sort: { type: 'string' }
      },
      {
         key: 'reason',
         header: 'REASON',
         width: '360px',
         maxChars: 130,
         sort: { type: 'string' }
      },
      {
         key: 'amount',
         header: 'AMOUNT',
         width: '115px',
         sort: {
            type: 'number',
            getValue: (row) => row.cells['amountRaw'],
            defaultDirection: 'asc'
         }
      },
      {
         key: 'user',
         header: 'USER',
         width: '140px',
         sort: { type: 'string' }
      }
   ],

   filter: {
      fields: [
         {
            key: 'date',
            label: 'Transaction Date',
            type: 'date',
            getValue: (row) => row.cells['dateRaw']
         },
         { key: 'time', label: 'Time', type: 'string' },
         { key: 'room', label: 'Room', type: 'string' },
         { key: 'guestName', label: 'Guest Name', type: 'string' },
         { key: 'transaction', label: 'Transaction', type: 'string' },
         { key: 'reason', label: 'Reason', type: 'string' },
         {
            key: 'amount',
            label: 'Amount',
            type: 'number',
            getValue: (row) => row.cells['amountRaw']
         },
         { key: 'user', label: 'User', type: 'string' }
      ]
   },

   flattenRows: flattenDetailedAdjustments,

   layouts: [
      {
         id: LAYOUT_DETAILED_ADJUSTMENTS,
         label: 'Detailed Adjustments',
         annotations: {
            enabled: true,
            selectableRowTypes: ['transaction'],
            disabledMessage:
               'Row comments and row highlighting are not available for this report layout. Please use the main report comments panel to add comments for this report.'
         },
         rowChecks: {
            enabled: false,
            includeNestedRows: false,
            selectableRowTypes: ['transaction']
         },
         columns: [
            {
               key: 'date',
               header: 'TRN DATE',
               width: '105px',
               sort: {
                  type: 'date',
                  getValue: (row) => row.cells['dateRaw']
               }
            },
            {
               key: 'time',
               header: 'TIME',
               width: '75px',
               sort: { type: 'string' }
            },
            {
               key: 'room',
               header: 'ROOM',
               width: '80px',
               sort: { type: 'string' }
            },
            {
               key: 'guestName',
               header: 'GUEST NAME',
               width: '160px',
               sort: { type: 'string' }
            },
            {
               key: 'transaction',
               header: 'TRANSACTION',
               width: '180px',
               sort: { type: 'string' }
            },
            {
               key: 'reason',
               header: 'REASON',
               width: '360px',
               maxChars: 130,
               sort: { type: 'string' }
            },
            {
               key: 'amount',
               header: 'AMOUNT',
               width: '115px',
               sort: {
                  type: 'number',
                  getValue: (row) => row.cells['amountRaw'],
                  defaultDirection: 'asc'
               }
            },
            {
               key: 'user',
               header: 'USER',
               width: '140px',
               sort: { type: 'string' }
            }
         ],
         filter: {
            fields: [
               {
                  key: 'date',
                  label: 'Transaction Date',
                  type: 'date',
                  getValue: (row) => row.cells['dateRaw']
               },
               { key: 'time', label: 'Time', type: 'string' },
               { key: 'room', label: 'Room', type: 'string' },
               { key: 'guestName', label: 'Guest Name', type: 'string' },
               { key: 'transaction', label: 'Transaction', type: 'string' },
               { key: 'reason', label: 'Reason', type: 'string' },
               {
                  key: 'amount',
                  label: 'Amount',
                  type: 'number',
                  getValue: (row) => row.cells['amountRaw']
               },
               { key: 'user', label: 'User', type: 'string' }
            ]
         },
         flattenRows: flattenDetailedAdjustments
      },
      {
         id: LAYOUT_ADJUSTMENTS_BY_USER,
         label: 'Adjustments by User',
         annotations: {
            enabled: false,
            disabledMessage:
               'Row comments and row highlighting are not available for Adjustments by User because this layout is calculated from multiple rows. Please use the main report comments panel to add comments for this report.'
         },
         rowChecks: {
            enabled: false,
            includeNestedRows: false,
            selectableRowTypes: ['userAdjustmentSummary']
         },
         columns: [
            {
               key: 'user',
               header: 'USER',
               width: '220px',
               sort: {
                  type: 'string',
                  getValue: (row) => row.cells['userRaw']
               }
            },
            {
               key: 'count',
               header: 'COUNT',
               width: '120px',
               sort: {
                  type: 'number',
                  defaultDirection: 'desc'
               }
            },
            {
               key: 'totalAdjusted',
               header: 'TOTAL ADJUSTED',
               width: '180px',
               sort: {
                  type: 'number',
                  getValue: (row) => row.cells['totalAdjustedRaw'],
                  defaultDirection: 'asc'
               }
            }
         ],
         filter: {
            fields: [
               {
                  key: 'user',
                  label: 'User',
                  type: 'string',
                  getValue: (row) => row.cells['userRaw']
               },
               {
                  key: 'count',
                  label: 'Count',
                  type: 'number'
               },
               {
                  key: 'totalAdjusted',
                  label: 'Total Adjusted',
                  type: 'number',
                  getValue: (row) => row.cells['totalAdjustedRaw']
               }
            ]
         },
         flattenRows: flattenAdjustmentsByUser
      }
   ]
};
